/**
 * Agent Training Academy — lesson content (gap G18).
 *
 * Kilat's revenue is paying operators multiplied by months retained, and agents
 * are the only channel that creates operators. The agent PWA has twelve routes
 * and every one of them is money or admin. For a brand-new agent with no
 * signups, every screen renders zero — the product is a money mirror that
 * reflects failure back at the person we most need to persist.
 *
 * This is the counterweight: something that has content in it on day one, and
 * progress that moves before any commission does.
 *
 * ## Why the content lives HERE and not in help articles
 *
 * The operator academy links out to the fourteen-article manual, because that
 * manual exists. There is no agent handbook, and there should not be: what an
 * agent needs is not reference material but the actual words to say in the
 * thirty seconds after walking into a carwash. Short enough to read on a
 * motorbike seat between visits, specific enough to repeat verbatim.
 *
 * So lessons carry i18n KEYS for real authored copy. The copy lives in the
 * shared catalog like every other string, which is what keeps it translatable —
 * an agent in Kelantan pitching in Malay needs the Malay words, not an English
 * script they have to improvise around.
 *
 * ## No quiz, no gate
 *
 * Lessons are marked done by the agent, not by a test. Self-report is enough:
 * the goal is that they have READ the thing before their first visit, and a quiz
 * would add friction to the exact moment we are trying to make frictionless.
 * Nothing in the product is locked behind the academy.
 *
 * Pure data plus pure functions: no I/O, no clock, no React.
 */

/** A rehearsed answer to something an operator will actually say. */
export interface ObjectionDrill {
  /** i18n key for the objection, in the operator's own words. */
  readonly objectionKey: string;
  /** i18n key for the reply — the exact words, not a strategy. */
  readonly replyKey: string;
}

export interface AgentLesson {
  readonly id: string;
  readonly titleKey: string;
  /** The teaching itself. Short: this is read standing up. */
  readonly bodyKey: string;
  /**
   * One concrete thing to do after reading. Every lesson has one — a lesson
   * that ends without a next action is a dead end, and a new agent reading a
   * screen that tells them nothing to do is exactly how the app becomes
   * something they stop opening.
   */
  readonly actionKey: string;
  /** Objections rehearsed in this lesson, if any. */
  readonly drills?: readonly ObjectionDrill[];
}

/**
 * The agent track, in the order a first visit actually happens.
 *
 * Deliberately front-loaded: lessons 1 and 2 are what someone needs before
 * walking into the first carwash, and everything after that is for the agent
 * who has already been told no once and is deciding whether to continue.
 */
export const AGENT_LESSONS: readonly AgentLesson[] = [
  {
    id: 'first-30-seconds',
    titleKey: 'agentAcademy.lesson.first30.title',
    bodyKey: 'agentAcademy.lesson.first30.body',
    actionKey: 'agentAcademy.lesson.first30.action',
  },
  {
    id: 'who-to-approach',
    titleKey: 'agentAcademy.lesson.whoToApproach.title',
    bodyKey: 'agentAcademy.lesson.whoToApproach.body',
    actionKey: 'agentAcademy.lesson.whoToApproach.action',
  },
  {
    id: 'the-demo',
    titleKey: 'agentAcademy.lesson.demo.title',
    bodyKey: 'agentAcademy.lesson.demo.body',
    actionKey: 'agentAcademy.lesson.demo.action',
  },
  {
    id: 'objections',
    titleKey: 'agentAcademy.lesson.objections.title',
    bodyKey: 'agentAcademy.lesson.objections.body',
    actionKey: 'agentAcademy.lesson.objections.action',
    drills: [
      {
        objectionKey: 'agentAcademy.drill.tooExpensive.objection',
        replyKey: 'agentAcademy.drill.tooExpensive.reply',
      },
      {
        objectionKey: 'agentAcademy.drill.alreadyHaveSystem.objection',
        replyKey: 'agentAcademy.drill.alreadyHaveSystem.reply',
      },
      {
        objectionKey: 'agentAcademy.drill.thinkAboutIt.objection',
        replyKey: 'agentAcademy.drill.thinkAboutIt.reply',
      },
      {
        objectionKey: 'agentAcademy.drill.staffCantUseIt.objection',
        replyKey: 'agentAcademy.drill.staffCantUseIt.reply',
      },
    ],
  },
  {
    id: 'rejection',
    titleKey: 'agentAcademy.lesson.rejection.title',
    bodyKey: 'agentAcademy.lesson.rejection.body',
    actionKey: 'agentAcademy.lesson.rejection.action',
  },
  {
    id: 'signing-them-up',
    titleKey: 'agentAcademy.lesson.signup.title',
    bodyKey: 'agentAcademy.lesson.signup.body',
    actionKey: 'agentAcademy.lesson.signup.action',
  },
  {
    id: 'after-the-signup',
    titleKey: 'agentAcademy.lesson.afterSignup.title',
    bodyKey: 'agentAcademy.lesson.afterSignup.body',
    actionKey: 'agentAcademy.lesson.afterSignup.action',
  },
];

/**
 * The next lesson to read, or `null` when there are none left.
 *
 * Order matters here in a way it does not for the operator track: an agent
 * asking "what do I do now" wants ONE answer, and the sequence is the answer.
 * Returns the first unread lesson in curriculum order rather than the lowest-
 * numbered gap, so an agent who skipped ahead is not dragged backwards.
 */
export function nextLesson(completedLessonIds: ReadonlySet<string>): AgentLesson | null {
  return AGENT_LESSONS.find((l) => !completedLessonIds.has(l.id)) ?? null;
}

/** How far through the track the agent is. */
export interface AgentAcademyProgress {
  readonly done: number;
  readonly total: number;
  /** 0..1, for a progress bar that means something on day one. */
  readonly fraction: number;
  readonly certified: boolean;
  readonly next: AgentLesson | null;
}

/**
 * Progress across the agent track.
 *
 * Ignores unknown ids rather than counting them. A lesson removed from the
 * curriculum must not leave an agent permanently at "8 of 7 read", and it must
 * not certify someone who never read what replaced it.
 */
export function agentProgress(completedLessonIds: ReadonlySet<string>): AgentAcademyProgress {
  const done = AGENT_LESSONS.filter((l) => completedLessonIds.has(l.id)).length;
  const total = AGENT_LESSONS.length;
  return {
    done,
    total,
    fraction: total === 0 ? 0 : done / total,
    certified: total > 0 && done === total,
    next: nextLesson(completedLessonIds),
  };
}

/**
 * Whether the agent has finished the track.
 *
 * Certification is a statement about effort, not competence — they read the
 * material, they did not pass an exam. It exists because an operator being
 * pitched should see someone who looks legitimate rather than a stranger with a
 * QR code, and because a new agent needs something to have achieved before
 * their first commission.
 */
export function isCertified(completedLessonIds: ReadonlySet<string>): boolean {
  return agentProgress(completedLessonIds).certified;
}
